import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const Unsubscribe = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [id, setId] = useState('');
  const [countdown, setCountdown] = useState(5);
  const [hasQueryParams, setHasQueryParams] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email');
    const idParam = urlParams.get('id');
    const hasParams = !!(emailParam || idParam);
    
    setHasQueryParams(hasParams);
    
    if (emailParam) {
      try {
        setEmail(atob(emailParam));
      } catch (e) {
        setEmail(emailParam);
      }
    }
    if (idParam) {
      setId(decodeURIComponent(idParam));
    }
  }, [location.search]);

  const handleUnsubscribe = async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/resend-unsubscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email || undefined,
          id: id || undefined,
        }),
      });
      
      if (!res.ok) {
        throw new Error('Failed to unsubscribe');
      }
      
      setStatus('success');
    } catch (error) {
      console.error(error);
      setStatus('error');
      setErrorMessage('There was a problem processing your request. Please try again or contact support.');
    }
  };

  // Countdown timer for redirect (only if successful)
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/');
    }
  }, [countdown, navigate, status]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-foreground mb-6">
            Unsubscribe
          </h1>
          
          {email && hasQueryParams && status !== 'success' && (
            <div className="mb-6 p-3 bg-muted rounded-md">
              <p className="text-sm text-muted-foreground mb-1">Email address:</p>
              <p className="text-foreground font-medium break-all">{email}</p>
            </div>
          )}
          
          {status === 'idle' && hasQueryParams && (
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Are you sure you want to unsubscribe from our mailing list? You will no longer receive updates or tips from us.
            </p>
          )}

          {status === 'loading' && (
            <p className="text-muted-foreground mb-8 leading-relaxed">
              Processing your request...
            </p>
          )}

          {status === 'success' && (
            <p className="text-muted-foreground mb-8 leading-relaxed text-green-600">
              You have been successfully unsubscribed from our mailing list.
            </p>
          )}

          {status === 'error' && (
            <p className="text-muted-foreground mb-8 leading-relaxed text-red-600">
              {errorMessage}
            </p>
          )}
          
          {status === 'idle' && !hasQueryParams && (
            <p className="text-muted-foreground mb-8 leading-relaxed">
              To unsubscribe, please click the unsubscribe link in one of our emails.
            </p>
          )}
          
          <div className="space-y-4">
            {status === 'idle' && hasQueryParams && (
              <div className="flex flex-col gap-3 pt-2">
                <button 
                  onClick={handleUnsubscribe}
                  className="inline-flex w-full items-center justify-center rounded-md bg-brand-pink text-primary-foreground hover:bg-brand-pink/90 h-10 px-6 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Yes, Unsubscribe Me
                </button>
                <button 
                  onClick={() => navigate('/')}
                  className="inline-flex w-full items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-6 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  Cancel
                </button>
              </div>
            )}

            {status === 'success' && (
              <p className="text-sm text-muted-foreground">
                Thank you for being part of our community.
              </p>
            )}
            
            {status === 'success' && (
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">
                  Redirecting to home page in:
                </p>
                <div className="text-3xl font-bold text-brand-pink">
                  {countdown}
                </div>
              </div>
            )}
            
            {(status === 'success' || status === 'error' || (!hasQueryParams && status === 'idle')) && (
              <div className="pt-4">
                <button 
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center rounded-md bg-brand-pink text-primary-foreground hover:bg-brand-pink/90 h-10 px-6 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Go to Home Now
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Unsubscribe;
